import * as Tooltip from '@radix-ui/react-tooltip';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function Pill({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'live' | 'locked' | 'positive' | 'overlay';
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase',
        tone === 'neutral' && 'bg-white/8 text-muted',
        // Sits on poster art, which can be any brightness, so it carries its own scrim.
        tone === 'overlay' && 'bg-shell/75 text-white backdrop-blur-sm',
        tone === 'accent' && 'bg-accent/15 text-accent-soft',
        tone === 'live' && 'bg-accent text-white',
        tone === 'locked' && 'bg-warn/15 text-warn',
        tone === 'positive' && 'bg-positive/15 text-positive',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  active?: boolean;
}

export function IconButton({ label, active, className, children, ...rest }: IconButtonProps) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'grid size-9 place-items-center rounded-xl border transition',
            'disabled:pointer-events-none disabled:opacity-35',
            active
              ? 'border-accent/50 bg-accent/15 text-accent-soft'
              : 'border-line text-muted hover:border-line hover:bg-hover hover:text-text',
            className,
          )}
          {...rest}
        >
          {children}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          sideOffset={6}
          className="border-line bg-raised text-text z-50 rounded-lg border px-2 py-1 text-xs shadow-xl"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
